import { css } from '@emotion/react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';
import { AttributionControl, Map, Marker, NavigationControl } from '@vis.gl/react-maplibre';
import { useState, useEffect } from 'react';

import '@/assets/css/maplibre-gl.css';
import { StyledButton } from '@/components/management/formElements/StyledButton';
import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';

const MapContainer = styled(Box)(
  ({ theme }) => css`
    height: 100dvh;

    width: 100%;
    border-radius: ${theme.spacing(1)};
    overflow: hidden;
    border: 1px solid ${theme.palette.divider};

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      height: 50dvh;
    }
  `,
);

interface FormMapProps {
  onLocationSelect: (lng: number, lat: number) => void;
  onClose: () => void;
  initialLongitude?: number;
  initialLatitude?: number;
  disabled?: boolean;
}

export function FormMap({
  onLocationSelect,
  onClose,
  initialLongitude = 8.7090022,
  initialLatitude = 50.1141545,
  disabled = false,
}: FormMapProps) {
  const [markerPosition, setMarkerPosition] = useState<[number, number]>([initialLongitude, initialLatitude]);

  useEffect(() => {
    setMarkerPosition([initialLongitude, initialLatitude]);
  }, [initialLongitude, initialLatitude]);

  const handleMapClick = (event: { lngLat: { lng: number; lat: number } }) => {
    if (disabled) return;
    const { lng, lat } = event.lngLat;
    const roundedLng = parseFloat(lng.toFixed(6));
    const roundedLat = parseFloat(lat.toFixed(6));
    setMarkerPosition([roundedLng, roundedLat]);
  };

  const handleMarkerDragEnd = (event: { lngLat: { lng: number; lat: number } }) => {
    if (disabled) return;
    const { lng, lat } = event.lngLat;
    const roundedLng = parseFloat(lng.toFixed(6));
    const roundedLat = parseFloat(lat.toFixed(6));
    setMarkerPosition([roundedLng, roundedLat]);
  };

  const handleConfirm = () => {
    onLocationSelect(markerPosition[0], markerPosition[1]);
    onClose();
  };

  return (
    <Dialog fullWidth open maxWidth="xl" onClose={onClose}>
      <DialogTitle>Position auf der Karte wählen</DialogTitle>
      <DialogContent>
        <MapContainer>
          <Map
            attributionControl={false}
            bearing={0}
            dragRotate={false}
            mapStyle="https://sgx.geodatenzentrum.de/gdz_basemapde_vektor/styles/bm_web_gry.json"
            maxZoom={18}
            minZoom={5}
            onClick={handleMapClick}
            pitch={0}
            style={{ cursor: disabled ? 'default' : 'crosshair' }}
            touchPitch={false}
            initialViewState={{
              longitude: markerPosition[0],
              latitude: markerPosition[1],
              zoom: 12,
            }}
          >
            <NavigationControl position="top-right" showCompass={false} />
            <AttributionControl compact position="bottom-right" />

            <Marker
              draggable={!disabled}
              latitude={markerPosition[1]}
              longitude={markerPosition[0]}
              onDragEnd={handleMarkerDragEnd}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: 'primary.main',
                  border: '3px solid white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  cursor: disabled ? 'default' : 'grab',
                  '&:active': {
                    cursor: disabled ? 'default' : 'grabbing',
                  },
                }}
              />
            </Marker>
          </Map>
        </MapContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="large" variant="outlined">
          Abbrechen
        </Button>
        <StyledButton label="Übernehmen" onClick={handleConfirm} />
      </DialogActions>
    </Dialog>
  );
}
